<script setup lang="ts">
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface Props {
  open: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'template-selected': [template: string]
}>()

const guideItems = [
  {
    number: '01',
    title: 'Ask a question',
    description: 'Type into the field and hit Enter (Shift+Enter for a new line). We answer in plain English — no ticket forms.'
  },
  {
    number: '02',
    title: 'Call human',
    description: 'Use the Call Human button to escalate to a human teammate when that option is enabled.'
  },
  {
    number: '03',
    title: 'Attach a file',
    description: 'Drop a screenshot, log, or document. We accept up to 10 MB per file.'
  },
  {
    number: '04',
    title: 'Start a new topic',
    description: 'Resets the current chat session and starts a fresh conversation when you want to change subjects.'
  },
  {
    number: '05',
    title: 'Minimize anytime',
    description: 'The widget collapses to a tab. Unread replies show a small indicator.'
  }
]

const guideDialogStyle = {
  width: 'min(1120px, calc(100vw - 64px))',
  maxWidth: 'min(1120px, calc(100vw - 64px))',
  maxHeight: 'min(1240px, calc(100vh - 64px))'
}
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <DialogContent
      class="abba-guide-dialog"
      portal-to="#abba-portal-container"
      :style="guideDialogStyle"
    >
      <DialogHeader class="abba-guide-header">
        <DialogTitle>How to use this chat</DialogTitle>
        <DialogDescription>
          Five things to know. You'll be productive in under a minute.
        </DialogDescription>
      </DialogHeader>

      <div class="abba-guide-body">
        <div
          v-for="item in guideItems"
          :key="item.number"
          class="abba-guide-step"
        >
          <div class="abba-guide-step-number">
            {{ item.number }}
          </div>
          <div class="abba-guide-step-copy">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </div>
        </div>
      </div>

      <div class="abba-guide-footer">
        <div class="abba-guide-esc">
          Press <span>Esc</span> to close
        </div>
        <Button
          class="abba-guide-cta"
          @click="emit('update:open', false)"
        >
          Got it
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
